import { CandidatStatusOnMission as S } from './enums';

/**
 * Statut affiché pour une mission TERMINÉE (liste « Terminées »), calculé à partir des candidatures ACTIVES uniquement.
 *
 * Règle : une candidature annulée (par l'agence, le candidat ou après signature), refusée ou archivée ne compte plus — elle ne doit
 * jamais faire afficher « En attente de validation ». Valable pour toutes les missions (fragmentables ou non, médecin ou infirmier).
 *
 *  - aucune candidature                                        → « Terminée sans affectation »
 *  - candidatures, aucune encore active                        → « Terminée sans affectation »
 *  - actives mais personne n'a abouti (candidature seule, contrat envoyé non signé, contrat à valider) → « Terminée sans affectation »
 *  - affecté (contrat signé / médecin confirmé)                → « En attente de validation » (heures à valider)
 *  - validée (en attente de paiement ou payée) → « Validée » ; validation refusée → « Validation refusée »
 */
export interface FinishedMissionStatus {
  label: string;
  /** Classe CSS de couleur (déjà utilisées par la table des missions). */
  css: 'required' | 'text-accent-green' | 'text-secondary' | 'text-accent-action';
  tooltip?: string;
}

const INACTIVE: string[] = [
  S.CANCELED_BY_CANDIDATE,
  S.CANCELED_BY_INTERIM_AGENCY,
  S.CANCELED_BY_ESTABLISHMENT,
  S.REJECTED,
  S.REJECTED_BY_ESTABLISHMENT_AFTER_SIGNING,
  S.ARCHIVED,
  S.CANCELLATION_ACCEPTED_AFTER_SIGNING,
];

/** Professionnel réellement AFFECTÉ : contrat signé (ou médecin confirmé) — seul cas où la mission peut être validée. */
export const ASSIGNED_STATUSES: string[] = [S.ACCEPTED_AND_SIGNED_BY_BOTH, S.WAITING_FOR_ESTABLISHMENT_VALIDATION];

export function isAssignedStatus(status?: string | null): boolean {
  return !!status && ASSIGNED_STATUSES.includes(status);
}

export function finishedMissionStatus(candidates: { statusOfCandidatOnMission?: string }[] | null | undefined): FinishedMissionStatus {
  const list = candidates ?? [];
  if (list.length === 0) {
    return { label: 'Terminée sans affectation', css: 'text-secondary', tooltip: "Aucune candidature n'a été reçue avant la fin de la mission." };
  }
  const active = list.map((c) => c.statusOfCandidatOnMission ?? '').filter((st) => st && !INACTIVE.includes(st));
  const has = (st: string) => active.includes(st);

  // Même libellé que pour une mission simple (« Validée ») : le suivi du paiement n'est pas affiché dans cette liste.
  if (has(S.PAYMENT_DONE) || has(S.MISSION_VALIDATED_WAITING_FOR_PAYMENT)) {
    return { label: 'Validée', css: 'text-accent-green' };
  }
  if (has(S.VALIDATION_REJECTED_BY_ESTABLISHMENT)) {
    return { label: "Validation refusée", css: 'required' };
  }
  if (has(S.WAITING_FOR_ESTABLISHMENT_VALIDATION) || has(S.ACCEPTED_AND_SIGNED_BY_BOTH)) {
    return { label: 'En attente de validation', css: 'required', tooltip: 'Mission terminée : les heures et la mission restent à valider.' };
  }
  return {
    label: 'Terminée sans affectation',
    css: 'text-secondary',
    tooltip: "Aucune candidature n'a abouti avant la fin de la mission (annulée, refusée, contrat non signé…).",
  };
}

/**
 * Statut d'une ligne « un seul professionnel » (listes de l'agence filtrées par professionnel) : le DTO ne porte pas la liste
 * `candidatesApplied` mais le statut de CE professionnel (`statusOfCandidatOnMission`).
 *  - mission terminée : mêmes règles que `finishedMissionStatus` (donc même libellé qu'une ligne « établissement »)
 *  - mission en cours : statut de la candidature
 * Valable pour médecin (signature automatique : jamais « Acceptée » / « Contrat en attente ») comme pour infirmier,
 * fragment comme mission simple.
 */
export function candidateScopeStatus(
  status: string | null | undefined,
  expired: boolean,
  missionStatus?: string | null
): FinishedMissionStatus {
  if (missionStatus === 'VALIDATED') {
    return { label: 'Validée', css: 'text-accent-green' };
  }
  if (missionStatus === 'INVALIDATED') {
    return { label: 'Validation refusée', css: 'required' };
  }
  const st = status ?? '';
  if (expired) {
    return finishedMissionStatus([{ statusOfCandidatOnMission: st }]);
  }
  switch (st) {
    case S.CANDIDAT_APPLIED_ON_THIS_MISSION:
      return { label: 'Candidature en attente', css: 'text-accent-action' };
    case S.CONTRACT_PENDING_VALIDATION:
      return { label: 'Contrat en attente de validation', css: 'text-accent-action' };
    case S.ACCEPTED:
      return { label: 'Acceptée', css: 'text-accent-action' };
    case S.ACCEPTED_AND_SIGNED_BY_BOTH:
    case S.WAITING_FOR_ESTABLISHMENT_VALIDATION:
      return { label: 'Offre totalement affectée', css: 'text-accent-green' };
    case S.MISSION_VALIDATED_WAITING_FOR_PAYMENT:
    case S.PAYMENT_DONE:
      return { label: 'Validée', css: 'text-accent-green' };
    case S.VALIDATION_REJECTED_BY_ESTABLISHMENT:
      return { label: 'Validation refusée', css: 'required' };
    case S.REJECTED:
      return { label: 'Candidature refusée', css: 'required' };
    case S.CANCELED_BY_CANDIDATE:
      return { label: 'Candidature annulée', css: 'required' };
    case S.CANCELED_BY_INTERIM_AGENCY:
      return { label: "Annulée par l'agence", css: 'required' };
    case S.CANCELED_BY_ESTABLISHMENT:
      return { label: "Annulée par l'établissement", css: 'required' };
    case S.CANCELLATION_REQUEST_AFTER_SIGN_CONTRACT:
      return { label: "Demande d'annulation", css: 'required' };
    case S.CANCELLATION_ACCEPTED_AFTER_SIGNING:
      return { label: 'Annulation acceptée', css: 'required' };
    case S.REJECTED_BY_ESTABLISHMENT_AFTER_SIGNING:
      return { label: 'Refusée après signature', css: 'required' };
    case S.ARCHIVED:
      return { label: 'Archivée', css: 'text-secondary' };
    case S.RESERVED:
      return { label: 'Réservée', css: 'text-secondary' };
    default:
      return { label: 'En cours', css: 'text-accent-action' };
  }
}

/** Clé du filtre « Statut mission » (liste Terminées) correspondant à l'état affiché d'une ligne « un professionnel ». */
export function finishedRowFilterKey(row: { statusOfCandidatOnMission?: string | null; missionStatus?: string | null }): string {
  const label = candidateScopeStatus(row.statusOfCandidatOnMission, true, row.missionStatus).label;
  switch (label) {
    case 'Validée':
    case 'Payée':
      return 'VALIDATED';
    case 'Validation refusée':
      return 'INVALIDATED';
    case 'En attente de validation':
      return 'AWAITING_VALIDATION';
    default:
      return 'NOT_ASSIGNED';
  }
}
